import { Button } from "@common/components/ui/button"
import { Checkbox } from "@common/components/ui/checkbox"
import { Input } from "@common/components/ui/input"
import { useRangeToggle } from "@common/hooks/use-range-toggle"
import { cn } from "@common/lib/utils"
import { useId, useState } from "react"
import Check from "~icons/material-symbols/check"
import Search from "~icons/material-symbols/search"

export type Option<T> = {
  value: T
  label: string
  sublabel?: string
  icon?: React.ReactNode
  disabled?: boolean
}

const CHECK_ROW =
  "relative flex select-none items-center gap-2 rounded px-2 py-1.5"

function centreInList(row: HTMLElement | null): void {
  const list = row?.parentElement
  if (!row || !list) return
  list.scrollTop = row.offsetTop - (list.clientHeight - row.offsetHeight) / 2
}

function SingleRow({
  label,
  selected,
  disabled,
  onPick,
}: {
  label: string
  selected: boolean
  disabled?: boolean
  onPick: () => void
}): React.ReactElement {
  return (
    <Button
      ref={selected ? centreInList : undefined}
      variant="ghost"
      size="sm"
      role="option"
      aria-selected={selected}
      disabled={disabled}
      className="w-full justify-start"
      onClick={onPick}
    >
      <span className="min-w-0 truncate">{label}</span>
      {selected && <Check className="ml-auto" />}
    </Button>
  )
}

export function MultiSelectBody<T extends string | number>({
  options,
  initial,
  isLoading,
  searchPlaceholder,
  emptyLabel,
  onApply,
  applyOnSelect = false,
  single = false,
  clearLabel,
  selectAllLabel,
  required = false,
  onSearch,
}: {
  options: Option<T>[]
  initial: T[]
  isLoading: boolean
  searchPlaceholder: string
  emptyLabel: string
  onApply: (values: T[]) => void
  applyOnSelect?: boolean
  single?: boolean
  clearLabel?: string
  selectAllLabel?: string
  required?: boolean
  onSearch?: (query: string) => void
}): React.ReactElement {
  const [query, setQuery] = useState("")
  const [selected, setSelected] = useState<Set<T>>(() => new Set(initial))
  const clearId = useId()

  const needle = query.toLowerCase()
  const filtered = onSearch
    ? options
    : options.filter(
        (option) =>
          option.label.toLowerCase().includes(needle) ||
          (option.sublabel?.toLowerCase().includes(needle) ?? false),
      )

  const showRows = !isLoading && filtered.length > 0

  const rangeToggle = useRangeToggle<T>()

  const enabled = filtered
    .filter((option) => !option.disabled)
    .map((option) => option.value)

  const clear = (): void => {
    setSelected(new Set())
    onApply([])
  }

  const toggle = (value: T, range: boolean): void => {
    const next = rangeToggle(enabled, selected, value, range)
    setSelected(next)
    if (applyOnSelect) onApply([...next])
  }

  return (
    <div className="flex flex-col">
      <div className="border-b p-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              onSearch?.(event.target.value)
            }}
            onKeyDown={(event) => event.stopPropagation()}
            placeholder={searchPlaceholder}
            className="pl-8"
          />
        </div>
      </div>
      <div
        role={single && showRows ? "listbox" : undefined}
        className={cn("max-h-64 overflow-y-auto p-1", single && "relative")}
        onWheel={(event) => event.stopPropagation()}
      >
        {isLoading ? (
          <p className="px-2 py-3 text-small-medium text-muted-foreground">
            Loading…
          </p>
        ) : filtered.length === 0 ? (
          <p className="px-2 py-3 text-small-medium text-muted-foreground">
            {emptyLabel}
          </p>
        ) : (
          <>
            {single && clearLabel !== undefined && needle === "" && (
              <SingleRow
                label={clearLabel}
                selected={selected.size === 0}
                onPick={() => onApply([])}
              />
            )}
            {!single &&
              applyOnSelect &&
              clearLabel !== undefined &&
              needle === "" && (
                <label
                  htmlFor={clearId}
                  className={cn(CHECK_ROW, "cursor-pointer hover:bg-accent")}
                >
                  <Checkbox
                    id={clearId}
                    checked={selected.size === 0}
                    className="after:absolute after:inset-0"
                    onClick={clear}
                    onPointerDown={(event) => event.stopPropagation()}
                  />
                  <span className="truncate text-small-medium">
                    {clearLabel}
                  </span>
                </label>
              )}
            {filtered.map((option) => {
              if (single) {
                return (
                  <SingleRow
                    key={String(option.value)}
                    label={option.label}
                    selected={selected.has(option.value)}
                    disabled={option.disabled}
                    onPick={() => onApply([option.value])}
                  />
                )
              }
              const id = `multi-select-opt-${String(option.value)}`
              return (
                <label
                  key={String(option.value)}
                  htmlFor={id}
                  className={cn(
                    CHECK_ROW,
                    option.disabled
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer hover:bg-accent",
                  )}
                >
                  <Checkbox
                    id={id}
                    checked={selected.has(option.value)}
                    disabled={option.disabled}
                    className="after:absolute after:inset-0"
                    onClick={(event) => toggle(option.value, event.shiftKey)}
                    onPointerDown={(event) => event.stopPropagation()}
                  />
                  {option.icon}
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-small-medium">
                      {option.label}
                    </span>
                    {option.sublabel && (
                      <span className="truncate text-small-regular text-muted-foreground">
                        {option.sublabel}
                      </span>
                    )}
                  </span>
                </label>
              )
            })}
          </>
        )}
      </div>
      {!applyOnSelect && !single && (
        <div className="flex justify-end gap-2 border-t p-2">
          {selectAllLabel !== undefined && (
            <Button
              variant="ghost"
              disabled={enabled.every((value) => selected.has(value))}
              onClick={() => setSelected(new Set([...selected, ...enabled]))}
            >
              {selectAllLabel}
            </Button>
          )}
          {clearLabel !== undefined && (
            <Button
              variant="ghost"
              disabled={selected.size === 0}
              onClick={() => setSelected(new Set())}
            >
              {clearLabel}
            </Button>
          )}
          <Button
            className="ml-auto"
            disabled={required && selected.size === 0}
            onClick={() => onApply([...selected])}
          >
            Apply
          </Button>
        </div>
      )}
    </div>
  )
}
